/* eslint no-unused-vars: ["error", { "argsIgnorePattern": "^_" }]*/
import React, { Fragment } from "react";

import cx from "classnames";

import { escapeRegEx } from "../../helpers/escapeRegex";
import { useUIDSeed } from "../../helpers/useUIDSeed";
import { Checkbox } from "../Checkbox";
import { Divider } from "../Divider";
import { IconButton } from "../IconButton";
import { Pill } from "../Pill";

import { RenderSuggestionProps } from "./MultiSelect";
import styles from "./MultiSelect.scss";
import { MultiSelectItem } from "./MultiSelectItem";
import NestedItem from "./NestedItem";

function renderText(text: string, currentValue: string): React.ReactNode {
  if (!text) {
    return "";
  }
  const regex = new RegExp(escapeRegEx(currentValue), "ig");
  const match = text.match(regex);
  let idx = -1;

  if (!currentValue || !match?.length) {
    return text;
  }

  return text.split(regex).reduce((res: React.ReactNode[], item: string) => {
    idx++;
    const boldText = match[idx] ? (
      <b key={`multi-select-${idx}`}>{match[idx]}</b>
    ) : (
      ""
    );
    return [...res, item, boldText];
  }, []);
}

export const VARIANTS = {
  BASIC: "basic",
  SYSTEM: "system",
} as const;

export type Variant = (typeof VARIANTS)[keyof typeof VARIANTS];

export interface MultiSelectSuggestionProps<T extends string = string> {
  item: MultiSelectItem<T> & {
    _treeLevel?: number;
    helperText?: string;
    user?: {
      avatar?: string;
      id?: string;
      initials?: string;
      jobTitle?: string;
      name?: string | React.ReactNode;
    };
  };
  isHighlighted?: boolean;
  isSelected?: boolean;
  currentInputValue?: string;
  indeterminate?: boolean;
  displayChildren?: boolean;
  isItemSelectable?: boolean;
  checkbox?: boolean;
  onDisplayChildrenChange?: (_item: MultiSelectItem<T>) => void;
  onChange?: (_event?: React.MouseEvent | React.ChangeEvent) => void;
  hasChildren?: boolean;
  isIndeterminate?: boolean;
  nestedData?: boolean;
  variant?: Variant;
  listId?: string;
  index?: number | string;
}

export const MultiSelectSuggestion = React.forwardRef<
  HTMLLIElement,
  MultiSelectSuggestionProps<string> & React.HTMLAttributes<HTMLLIElement>
>(
  // eslint-disable-next-line max-lines-per-function
  (
    {
      item,
      isHighlighted,
      isSelected,
      currentInputValue = "",
      indeterminate,
      displayChildren,
      isItemSelectable,
      checkbox,
      onDisplayChildrenChange,
      onChange,
      hasChildren,
      isIndeterminate,
      nestedData,
      variant = VARIANTS.BASIC,
      listId,
      index,
      ...menuProps
    },
    ref
  ) => {
    const generateUID = useUIDSeed(listId);
    const itemId = generateUID(index?.toString() ?? "");
    const pillClassName = cx(styles["checkbox-pill"], {
      [styles["disabled-pill"]]: !isItemSelectable,
    });

    function wrapContent(content: React.ReactNode): React.ReactNode {
      return (
        <>
          <Divider className={styles["checkbox-divider"]} />
          <div
            className={
              item.user
                ? styles["person-checkbox-wrapper"]
                : styles["checkbox-wrapper"]
            }
            style={{ marginLeft: `${(item._treeLevel || 0) * 7}px` }}
          >
            {content}
            {item.pillText && (
              <>
                <span className="screen-reader-only">,</span>
                <Pill
                  data-testid="checkbox-pill"
                  text={item.pillText}
                  size="small"
                  className={pillClassName}
                />
              </>
            )}
          </div>
        </>
      );
    }

    /**
     * :::::::::::::::: 4 List Item Styles ::::::::::::::::
     * 1. no checkbox && no nestedData (normal style)
     * 2. no checkbox && nestedData  (nested style, single select)
     * 3. checkbox && no nestedData  (normal style)
     * 4. checkbox && nestedData (nested style, multiple select)
     */
    const renderItem = (): React.ReactNode => {
      if (variant === "system") {
        return (
          <Fragment>
            <div className={styles["suggestion-container"]}>
              <div className={styles["suggestion-text"]}>
                <span> {renderText(item.text, currentInputValue)}</span>
              </div>
              <div className={styles["suggestion-helper-text"]}>
                <span className="screen-reader-only">,</span>
                <span>
                  {" "}
                  {renderText(item.helperText || "", currentInputValue)}
                </span>
              </div>
            </div>
          </Fragment>
        );
      }

      /* Style 1 */
      if (!checkbox && !nestedData) {
        if (item.pillText) {
          return renderText(item.pillText, currentInputValue);
        }
        return renderText(item.text, currentInputValue);
      }

      let content: React.ReactNode;
      const iconButton = (
        <IconButton
          data-testid="multiselect-item-caret-test"
          key={`Icon-${item.id}`}
          className={styles["list-caret"]}
          icon={displayChildren ? "caret_up" : "caret_dn"}
          iconVariant="regular"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            isItemSelectable &&
              onDisplayChildrenChange &&
              onDisplayChildrenChange(item);
          }}
          system={false}
          type="button"
        />
      );

      /* Style 2 */
      if (!checkbox && nestedData) {
        content = (
          <div>
            {renderText(item.text, currentInputValue)}
            {hasChildren && iconButton}
          </div>
        );
      } else {
        /* Style 3 and 4  */
        content = (
          <Checkbox
            id={item.id}
            indeterminate={isIndeterminate}
            onIndeterminateChange={onChange}
            data-testid="multiselect-checkbox-item-test"
            checked={isSelected}
            disabled={!isItemSelectable}
            className={!isItemSelectable ? styles["check-box-disabled"] : ""}
            label={[
              renderText(item.text, currentInputValue),
              hasChildren && iconButton,
            ]}
            helperText={
              <>
                <span className="screen-reader-only">,</span>
                {renderText(item.helperText || " ", currentInputValue)}
              </>
            }
            onChange={onChange}
            user={item.user}
          />
        );
      }

      return wrapContent(content);
    };

    return (
      <li
        className={cx(styles["suggestion-row"], {
          [styles["suggestion-row--highlighted"]]: isHighlighted,
          [styles["suggestion-row--checkbox"]]: checkbox,
          [styles["suggestion-row--border-bottom"]]: variant === "system",
        })}
        key={item.id}
        id={itemId}
        role="option"
        aria-selected={isHighlighted}
        ref={ref}
        onClick={isItemSelectable && !indeterminate ? onChange : undefined}
        onKeyDown={() => {}}
        data-testid="multiselect-list-item-test"
        {...menuProps}
      >
        {renderItem()}
      </li>
    );
  }
);

export type RenderDefaultSuggestionProps<T extends string> =
  RenderSuggestionProps<T>;

export const renderDefaultSuggestion = <T extends string>({
  item,
  ...props
}: RenderDefaultSuggestionProps<T>): React.ReactElement => {
  return <NestedItem<T> key={item.id} item={item} {...props} />;
};

export default MultiSelectSuggestion;
